import { WORLD_H, WORLD_W } from '../core/constants';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Maps the 1200 x 800 plate into a screen rectangle. On tall (portrait) slots the whole plate is
 * turned a quarter turn clockwise, so phones get a large board; the physics does not care.
 */
export class View {
  /** CSS pixels per world unit. */
  scale = 1;
  rotated = false;
  /** Plate rectangle on screen (CSS px). */
  plate: Rect = { x: 0, y: 0, w: WORLD_W, h: WORLD_H };
  private ox = 0;
  private oy = 0;

  fit(slot: Rect, allowRotate = true, margin = 0): void {
    const avW = Math.max(10, slot.w - margin * 2);
    const avH = Math.max(10, slot.h - margin * 2);
    this.rotated = allowRotate && avH > avW * 1.05;
    const pw = this.rotated ? WORLD_H : WORLD_W;
    const ph = this.rotated ? WORLD_W : WORLD_H;
    this.scale = Math.min(avW / pw, avH / ph);
    const w = pw * this.scale;
    const hh = ph * this.scale;
    this.ox = slot.x + (slot.w - w) / 2;
    this.oy = slot.y + (slot.h - hh) / 2;
    this.plate = { x: this.ox, y: this.oy, w, h: hh };
  }

  /** World to screen (CSS px). */
  toScreen(x: number, y: number): [number, number] {
    const s = this.scale;
    return this.rotated
      ? [this.ox + s * (WORLD_H - y), this.oy + s * x]
      : [this.ox + s * x, this.oy + s * y];
  }

  /** Screen (CSS px) to world. */
  toWorld(sx: number, sy: number): [number, number] {
    const s = this.scale;
    return this.rotated
      ? [(sy - this.oy) / s, WORLD_H - (sx - this.ox) / s]
      : [(sx - this.ox) / s, (sy - this.oy) / s];
  }

  /** Canvas transform for drawing in world units (multiply by device pixel ratio). */
  matrix(dpr: number): [number, number, number, number, number, number] {
    const s = this.scale * dpr;
    return this.rotated
      ? [0, s, -s, 0, (this.ox + this.scale * WORLD_H) * dpr, this.oy * dpr]
      : [s, 0, 0, s, this.ox * dpr, this.oy * dpr];
  }

  /** Screen direction of a world angle (radians). */
  screenAngle(a: number): number {
    return this.rotated ? a + Math.PI / 2 : a;
  }

  /** World displacement for a screen-space arrow key step (dx, dy in screen axes). */
  screenStepToWorld(dx: number, dy: number): [number, number] {
    // Rotated: screen right = world -y, screen down = world +x.
    return this.rotated ? [dy, -dx] : [dx, dy];
  }
}
